import React, { useState, useRef, useEffect } from 'react';
import * as api from '../api';




function FieldGroup({ title, children }) {
  return (
    <div className="card" style={{ marginBottom: '20px' }}>
      <h3 style={{
        fontSize: '15px',
        fontWeight: '700',
        color: '#1a1a2e',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #4299e1'
      }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#4a5568',
        marginBottom: '6px'
      }}>
        {label}
      </label>
      {children}
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  borderRadius: '6px',
  border: '1px solid #e2e8f0',
  fontSize: '14px',
  outline: 'none',
  color: '#1a1a2e',
  backgroundColor: 'white'
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
  gap: '0 24px'
};

function PresenterSearch({ database, presenterOrder, onAdd }) {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  const suggestions = query.trim().length === 0 ? [] : database.filter(p => {
    const alreadyAdded = presenterOrder.find(op => op.id === p.id);
    return !alreadyAdded && p.name.toLowerCase().includes(query.toLowerCase());
  });

  const handleSelect = (presenter) => {
    onAdd(presenter);
    setQuery('');
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ position: 'relative', marginBottom: '16px' }}>
      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Type a presenter name to search the database..."
          value={query}
          onChange={e => {
            setQuery(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          style={{
            ...inputStyle,
            flex: 1,
            border: '1px solid #4299e1',
          }}
        />
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
          marginTop: '4px',
          overflow: 'hidden'
        }}>
          {suggestions.map(presenter => (
            <div
              key={presenter.id}
              onMouseDown={() => handleSelect(presenter)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                cursor: 'pointer',
                borderBottom: '1px solid #f7fafc',
                transition: 'background 0.1s'
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#ebf8ff'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'white'}
            >
              <div style={{
                width: '32px', height: '32px',
                borderRadius: '50%',
                backgroundColor: '#e2e8f0',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px', flexShrink: 0,
                overflow: 'hidden'
              }}>
                {presenter.photo
                  ? <img src={presenter.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : '👤'}
              </div>
              <div>
                <p style={{ fontWeight: '600', fontSize: '13px', color: '#1a1a2e' }}>{presenter.name}</p>
                <p style={{ fontSize: '12px', color: '#718096' }}>{presenter.jobTitle} — {presenter.company}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* No match message */}
      {showSuggestions && query.trim().length > 0 && suggestions.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #e2e8f0',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 100,
          marginTop: '4px',
          padding: '14px',
          fontSize: '13px',
          color: '#a0aec0',
          fontStyle: 'italic'
        }}>
          No presenter found matching "{query}" — add them in the Database page first.
        </div>
      )}
    </div>
  );
}

function JobInfo({ database, jobInfo, setJobInfo, showPresenters, setShowPresenters, currentShow, currentUser, allUsers, onAddPermission, onRemovePermission, onChangeOwner, viewOnly }) {
  const availablePresenters = database || [];
  const [dragging, setDragging] = useState(null);
  const [dragOver, setDragOver] = useState(null);

  const updateField = (field, value) => {
    if (viewOnly) return;
    setJobInfo(prev => ({ ...prev, [field]: value }));
  };

  const updateStreamKey = (index, field, value) => {
    if (viewOnly) return;
    const updated = [...(jobInfo.streamKeys || [])];
    updated[index] = { ...updated[index], [field]: value };
    setJobInfo(prev => ({ ...prev, streamKeys: updated }));
  };

  const addStreamKey = () => {
    if (viewOnly) return;
    setJobInfo(prev => ({
      ...prev,
      streamKeys: [...(prev.streamKeys || []), { label: '', key: '' }]
    }));
  };

  const removeStreamKey = (index) => {
    if (viewOnly) return;
    setJobInfo(prev => ({
      ...prev,
      streamKeys: (prev.streamKeys || []).filter((_, i) => i !== index)
    }));
  };

  const addPresenter = (presenter) => {
    if (viewOnly) return;
    if (showPresenters.find(p => p.id === presenter.id)) return;
    setShowPresenters(prev => [...prev, presenter]);
  };

  const removePresenter = (id) => {
    if (viewOnly) return;
    setShowPresenters(prev => prev.filter(p => p.id !== id));
  };

  const handleDragStart = (index) => setDragging(index);

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOver(index);
  };

  const handleDrop = (index) => {
    if (dragging === null || dragging === index) return;
    const updated = [...showPresenters];
    const item = updated.splice(dragging, 1)[0];
    updated.splice(index, 0, item);
    setShowPresenters(updated);
    setDragging(null);
    setDragOver(null);
  };

  if (viewOnly) {
    return (
      <div>
        <div className="page-header">
          <h1>Job Info</h1>
          <p>View only — you do not have permission to edit this show.</p>
        </div>
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '6px',
          padding: '10px 16px',
          marginBottom: '20px',
          color: '#c53030',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          👁️ View Only — You do not have permission to edit this show.
        </div>

        <FieldGroup title="📋  Event Details">
          <div style={gridStyle}>
            {[
              { label: 'Job Title', value: jobInfo.jobTitle },
              { label: 'Show Dates', value: jobInfo.showDates },
              { label: 'Setup Dates', value: jobInfo.setupDates },
            ].map(f => (
              <Field key={f.label} label={f.label}>
                <div style={{ ...inputStyle, backgroundColor: '#f7fafc', color: '#1a1a2e' }}>{f.value || '—'}</div>
              </Field>
            ))}
          </div>
          <div style={gridStyle}>
            {[
              { label: 'Crew Call Time', value: jobInfo.crewCallTime },
              { label: 'Doors Open Time', value: jobInfo.doorsOpenTime },
              { label: 'Show Start Time', value: jobInfo.showStartTime },
            ].map(f => (
              <Field key={f.label} label={f.label}>
                <div style={{ ...inputStyle, backgroundColor: '#f7fafc', color: '#1a1a2e' }}>{f.value || '—'}</div>
              </Field>
            ))}
          </div>
        </FieldGroup>

        <FieldGroup title="💻  Zoom & Streaming Info">
          <div style={gridStyle}>
            {[
              { label: 'Zoom Producer Name', value: jobInfo.zoomProducerName },
              { label: 'Zoom Webinar ID', value: jobInfo.zoomWebinarId },
              { label: 'Zoom Webinar Passcode', value: jobInfo.zoomWebinarPasscode },
              { label: 'Zoom Presenter ID', value: jobInfo.zoomPresenterId },
              { label: 'Zoom Presenter Passcode', value: jobInfo.zoomPresenterPasscode },
            ].map(f => (
              <Field key={f.label} label={f.label}>
                <div style={{ ...inputStyle, backgroundColor: '#f7fafc', color: '#1a1a2e' }}>{f.value || '—'}</div>
              </Field>
            ))}
          </div>
          {(jobInfo.streamKeys || []).length > 0 && (
            <div style={{ marginTop: '8px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '10px' }}>
                Stream Keys
              </label>
              {jobInfo.streamKeys.map((sk, index) => (
                <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                  <div style={{ ...inputStyle, width: '180px', backgroundColor: '#f7fafc' }}>{sk.label}</div>
                  <div style={{ ...inputStyle, flex: 1, fontFamily: 'monospace', backgroundColor: '#f7fafc' }}>{sk.key}</div>
                </div>
              ))}
            </div>
          )}
        </FieldGroup>

        <FieldGroup title="🎤  Presenters (In Order of Appearance)">
          {showPresenters.length === 0 ? (
            <p style={{ color: '#a0aec0', fontSize: '13px', fontStyle: 'italic' }}>No presenters added.</p>
          ) : (
            <div>
              {showPresenters.map((presenter, index) => (
                <div key={presenter.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '8px',
                  backgroundColor: '#f7fafc'
                }}>
                  <span style={{
                    backgroundColor: '#1a1a2e',
                    color: 'white',
                    borderRadius: '50%',
                    width: '24px', height: '24px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '11px', fontWeight: '700', flexShrink: 0
                  }}>
                    {index + 1}
                  </span>
                  <div style={{
                    width: '36px', height: '36px',
                    borderRadius: '50%',
                    backgroundColor: '#e2e8f0',
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '18px', flexShrink: 0,
                    overflow: 'hidden'
                  }}>
                    {presenter.photo
                      ? <img src={presenter.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : '👤'}
                  </div>
                  <div>
                    <p style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{presenter.name}</p>
                    <p style={{ fontSize: '12px', color: '#718096' }}>{presenter.jobTitle} — {presenter.company}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </FieldGroup>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Job Info</h1>
        <p>{viewOnly ? 'View only — you do not have permission to edit this show.' : 'Enter the overall details for this event. All fields auto-save as you type.'}</p>
      </div>
      {viewOnly && (
        <div style={{
          backgroundColor: '#fff5f5',
          border: '1px solid #fed7d7',
          borderRadius: '6px',
          padding: '10px 16px',
          marginBottom: '20px',
          color: '#c53030',
          fontSize: '13px',
          fontWeight: '600'
        }}>
          👁️ View Only — You do not have permission to edit this show.
        </div>
      )}

      <div className={viewOnly ? 'view-only-container' : ''} style={{ opacity: viewOnly ? 0.7 : 1 }}>
      {/* Event Details */}
      <FieldGroup title="📋  Event Details">
        <div style={gridStyle}>
          <Field label="Job Title">
            <input
              style={{...inputStyle, backgroundColor: viewOnly ? '#f7fafc' : 'white'}}
              type="text"
              placeholder="e.g. Acme Corp Annual Conference 2026"
              value={jobInfo.jobTitle}
              onChange={e => updateField('jobTitle', e.target.value)}
              readOnly={viewOnly}
              disabled={viewOnly}
            />
          </Field>
          <Field label="Show Dates">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. March 15, 2026"
              value={jobInfo.showDates}
              onChange={e => updateField('showDates', e.target.value)}
            />
          </Field>
          <Field label="Setup Dates">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. March 14, 2026"
              value={jobInfo.setupDates}
              onChange={e => updateField('setupDates', e.target.value)}
            />
          </Field>
        </div>
        <div style={gridStyle}>
          <Field label="Crew Call Time">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. 6:00 AM"
              value={jobInfo.crewCallTime}
              onChange={e => updateField('crewCallTime', e.target.value)}
            />
          </Field>
          <Field label="Doors Open Time">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. 7:30 AM"
              value={jobInfo.doorsOpenTime}
              onChange={e => updateField('doorsOpenTime', e.target.value)}
            />
          </Field>
          <Field label="Show Start Time">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. 8:00 AM"
              value={jobInfo.showStartTime}
              onChange={e => updateField('showStartTime', e.target.value)}
            />
          </Field>
        </div>
      </FieldGroup>

      {/* Zoom Info */}
      <FieldGroup title="💻  Zoom & Streaming Info">
        <div style={gridStyle}>
          <Field label="Zoom Producer Name">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. Alex Rivera"
              value={jobInfo.zoomProducerName}
              onChange={e => updateField('zoomProducerName', e.target.value)}
            />
          </Field>
        </div>
        <div style={gridStyle}>
          <Field label="Zoom Webinar ID">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. 123 456 7890"
              value={jobInfo.zoomWebinarId}
              onChange={e => updateField('zoomWebinarId', e.target.value)}
            />
          </Field>
          <Field label="Zoom Webinar Passcode">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. showday2026"
              value={jobInfo.zoomWebinarPasscode}
              onChange={e => updateField('zoomWebinarPasscode', e.target.value)}
            />
          </Field>
          <Field label="Zoom Presenter ID">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. 987 654 3210"
              value={jobInfo.zoomPresenterId}
              onChange={e => updateField('zoomPresenterId', e.target.value)}
            />
          </Field>
          <Field label="Zoom Presenter Passcode">
            <input
              style={inputStyle}
              type="text"
              placeholder="e.g. presenter2026"
              value={jobInfo.zoomPresenterPasscode}
              onChange={e => updateField('zoomPresenterPasscode', e.target.value)}
            />
          </Field>
        </div>

        {/* Stream Keys */}
        <div style={{ marginTop: '8px' }}>
          <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '10px' }}>
            Stream Keys
          </label>
          {(jobInfo.streamKeys || []).map((sk, index) => (
            <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center' }}>
              <input
                style={{ ...inputStyle, width: '180px' }}
                type="text"
                placeholder="Label (e.g. YouTube)"
                value={sk.label}
                onChange={e => updateStreamKey(index, 'label', e.target.value)}
              />
              <input
                style={{ ...inputStyle, flex: 1, fontFamily: 'monospace' }}
                type="text"
                placeholder="Stream key"
                value={sk.key}
                onChange={e => updateStreamKey(index, 'key', e.target.value)}
              />
              {jobInfo.streamKeys.length > 1 && (
                <button
                  onClick={() => removeStreamKey(index)}
                  style={{
                    background: 'none',
                    border: '1px solid #fed7d7',
                    borderRadius: '6px',
                    padding: '8px 12px',
                    color: '#c53030',
                    cursor: 'pointer',
                    fontSize: '16px'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addStreamKey}
            style={{
              background: 'none',
              border: '1px dashed #4299e1',
              borderRadius: '6px',
              padding: '8px 16px',
              color: '#4299e1',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600'
            }}
          >
            + Add Stream Key
          </button>
        </div>
      </FieldGroup>

      {/* Presenters */}
      <FieldGroup title="🎤  Presenters (In Order of Appearance)">
        <p style={{ fontSize: '13px', color: '#718096', marginBottom: '14px' }}>
          Search for a presenter by name. If they are in the database they will appear as a suggestion. Drag the rows below to reorder.
        </p>

        <PresenterSearch
          database={availablePresenters}
          presenterOrder={showPresenters}
          onAdd={addPresenter}
        />

        {/* Presenter Order List */}
        {showPresenters.length === 0 ? (
          <div style={{
            padding: '32px',
            borderRadius: '8px',
            border: '2px dashed #e2e8f0',
            textAlign: 'center',
            color: '#a0aec0',
            fontSize: '13px',
            marginTop: '8px'
          }}>
            No presenters added yet. Search above to add your first presenter.
          </div>
        ) : (
          <div style={{ marginTop: '8px' }}>
            {showPresenters.map((presenter, index) => (
              <div
                key={presenter.id}
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={e => handleDragOver(e, index)}
                onDrop={() => handleDrop(index)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 14px',
                  borderRadius: '8px',
                  border: `1px solid ${dragOver === index ? '#4299e1' : '#e2e8f0'}`,
                  marginBottom: '8px',
                  backgroundColor: dragOver === index ? '#ebf8ff' : 'white',
                  cursor: 'grab'
                }}
              >
                <span style={{ color: '#a0aec0', fontSize: '18px' }}>⠿</span>
                <span style={{
                  backgroundColor: '#1a1a2e',
                  color: 'white',
                  borderRadius: '50%',
                  width: '24px', height: '24px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '700', flexShrink: 0
                }}>
                  {index + 1}
                </span>
                <div style={{
                  width: '36px', height: '36px',
                  borderRadius: '50%',
                  backgroundColor: '#e2e8f0',
                  display: 'flex', alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px', flexShrink: 0,
                  overflow: 'hidden'
                }}>
                  {presenter.photo
                    ? <img src={presenter.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : '👤'}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a2e' }}>{presenter.name}</p>
                  <p style={{ fontSize: '12px', color: '#718096' }}>{presenter.jobTitle} — {presenter.company}</p>
                </div>
                <button
                  onClick={() => removePresenter(presenter.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#a0aec0',
                    fontSize: '20px',
                    cursor: 'pointer',
                    lineHeight: 1
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </FieldGroup>
      </div>
      {/* Show Permissions */}
      {(currentUser?.role === 'admin' || currentShow?.userPermission === 'owner') && (
        <div>
        <FieldGroup title="🔒  Show Permissions">
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>
              Owner
            </p>
            {currentUser?.role === 'admin' ? (
              <select
                value={(allUsers || []).find(u => u.name === currentShow?.ownerName)?.id || ''}
                onChange={e => {
                  if (e.target.value) onChangeOwner(e.target.value);
                }}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  fontSize: '14px',
                  outline: 'none',
                  color: '#1a1a2e',
                  backgroundColor: 'white'
                }}
              >
                <option value="">Select owner...</option>
                {(allUsers || []).filter(u => u.role !== 'viewer').map(u => (
                  <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                ))}
              </select>
            ) : (
              <p style={{ fontSize: '13px', color: '#718096' }}>
                <strong>{currentShow?.ownerName || 'Unknown'}</strong>
              </p>
            )}
          </div>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '10px' }}>
            Editors
          </p>
          {(currentShow?.editors || []).length === 0 ? (
            <p style={{ fontSize: '13px', color: '#a0aec0', fontStyle: 'italic', marginBottom: '14px' }}>
              No editors added yet.
            </p>
          ) : (
            <div style={{ marginBottom: '14px' }}>
              {(currentShow?.editors || []).map(editor => (
                <div key={editor.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e2e8f0',
                  marginBottom: '6px',
                  backgroundColor: '#f7fafc'
                }}>
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>{editor.name}</span>
                    <span style={{ fontSize: '12px', color: '#718096', marginLeft: '8px' }}>{editor.email}</span>
                  </div>
                  <button
                    onClick={() => onRemovePermission(editor.id)}
                    style={{
                      background: 'none',
                      border: '1px solid #fed7d7',
                      borderRadius: '4px',
                      padding: '3px 8px',
                      color: '#c53030',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#4a5568', marginBottom: '8px' }}>
            Add Editor
          </p>
          <select
            onChange={e => {
              if (e.target.value) {
                onAddPermission(e.target.value, 'editor');
                e.target.value = '';
              }
            }}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #e2e8f0',
              fontSize: '14px',
              outline: 'none',
              color: '#1a1a2e',
              backgroundColor: 'white'
            }}
          >
            <option value="">Select a user to add as editor...</option>
            {(allUsers || [])
              .filter(u => 
                u.id !== currentUser?.id &&
                !(currentShow?.editors || []).find(e => e.id === u.id) &&
                currentShow?.ownerName !== u.name
              )
              .map(u => (
                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
              ))
            }
          </select>
        </FieldGroup>
        </div>
      )}

      {(currentUser?.role === 'admin' || currentShow?.userPermission === 'owner') && (
        <div style={{ paddingBottom: '200px' }}>
          <SnapshotPanel showId={currentShow?.id} />
        </div>
      )}
    </div>
  );
}

function SnapshotPanel({ showId }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');

  const loadSnapshots = async () => {
    if (!showId) return;
    setLoading(true);
    try {
      const data = await api.getSnapshots(showId);
      setSnapshots(Array.isArray(data) ? data : []);
    } catch (err) {
      setMessage('Could not load snapshots.');
    }
    setLoading(false);
  };

  const handleOpen = () => {
    const next = !open;
    setOpen(next);
    if (next) loadSnapshots();
  };

  const handleRestore = async (snapshotId, createdAt) => {
    const when = new Date(createdAt).toLocaleString();
    if (!window.confirm(`Restore this show to how it looked at ${when}?\n\nA backup of the current state will be saved first, so this can be undone.`)) return;
    try {
      const result = await api.restoreSnapshot(showId, snapshotId);
      if (result.error) {
        setMessage(result.error);
        return;
      }
      setMessage('Restored. Reloading...');
      setTimeout(() => window.location.reload(), 1200);
    } catch (err) {
      setMessage('Restore failed.');
    }
  };

  return (
    <FieldGroup title="🕘  Version History">
      <p style={{ fontSize: '13px', color: '#718096', marginBottom: '14px' }}>
        Automatic backups are taken every 5 minutes while a show is being edited. The 40 most recent are kept.
      </p>

      {message && (
        <div style={{
          backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', borderRadius: '6px',
          padding: '10px', marginBottom: '12px', color: '#2b6cb0', fontSize: '13px'
        }}>
          {message}
        </div>
      )}

      <button
        onClick={handleOpen}
        style={{
          backgroundColor: open ? '#4299e1' : '#e2e8f0',
          color: open ? 'white' : '#1a1a2e',
          border: 'none', borderRadius: '6px', padding: '8px 16px',
          fontSize: '13px', fontWeight: '600', cursor: 'pointer'
        }}
      >
        {open ? 'Hide History' : 'Show History'}
      </button>

      {open && (
        <div style={{ marginTop: '14px' }}>
          {loading ? (
            <p style={{ fontSize: '13px', color: '#718096' }}>Loading...</p>
          ) : snapshots.length === 0 ? (
            <p style={{ fontSize: '13px', color: '#a0aec0', fontStyle: 'italic' }}>
              No backups yet. They begin accumulating as the show is edited.
            </p>
          ) : (
            snapshots.map(snap => (
              <div key={snap.id} style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '8px 12px', borderRadius: '6px', border: '1px solid #e2e8f0',
                marginBottom: '6px', backgroundColor: '#f7fafc'
              }}>
                <div>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a2e' }}>
                    {new Date(snap.created_at).toLocaleString()}
                  </span>
                  <span style={{ fontSize: '12px', color: '#718096', marginLeft: '10px' }}>
                    {snap.row_count} row{snap.row_count !== 1 ? 's' : ''}
                    {snap.created_by_name ? ` · ${snap.created_by_name}` : ''}
                  </span>
                </div>
                <button
                  onClick={() => handleRestore(snap.id, snap.created_at)}
                  style={{
                    background: 'none', border: '1px solid #bee3f8', borderRadius: '4px',
                    padding: '4px 10px', color: '#2b6cb0', cursor: 'pointer', fontSize: '12px', fontWeight: '600'
                  }}
                >
                  Restore
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </FieldGroup>
  );
}

export default JobInfo;